(() => {
  const PICKER_SELECTOR = 'variant-picker';
  const FILTER_SELECTOR = '[data-personalization-filter]';
  const TOGGLE_SELECTOR = '[data-personalization-filter-input]';
  const SWATCH_SELECTOR =
    '.option__fieldset--swatch input[type="radio"], .design-picker__item input[type="radio"]';
  const RESOLVED_ATTRIBUTE = 'data-personalizable';
  const SOLD_OUT_ATTRIBUTE = 'data-sold-out';
  const ACTIVE_CLASS = 'variant-picker--personalizable-only';
  const OFFER_CHANNEL = 'D2C';
  const AVAILABLE_OFFER_STATES = new Set(['ACTIVE', 'MANUALLY_SET_TO_ACTIVE']);
  const READY_TIMEOUT = 10000;
  const LOOKUP_BATCH_SIZE = 30;
  const RESOLVE_DEBOUNCE = 100;

  const availabilityByVariantId = new Map();
  let readySdk = null;
  let filterEnabled = false;
  let resolveTimer = null;

  const withTimeout = (promise, timeout) =>
    Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), timeout))]);

  const whenScriptLoaded = () =>
    new Promise((resolve) => {
      if (!window.Stylux) {
        resolve(null);
        return;
      }

      window.Stylux.onScriptLoad(() => resolve(window.Stylux));
    });

  const whenReady = async () => {
    const sdk = await whenScriptLoaded();
    if (!sdk) return null;

    try {
      const isSetup = await withTimeout(sdk.setupComplete().then(() => true), READY_TIMEOUT);
      return isSetup ? sdk : null;
    } catch (error) {
      console.error('Stylux SDK setup failed:', error);
      return null;
    }
  };

  const getSdk = () => {
    if (!readySdk) readySdk = whenReady();
    return readySdk;
  };

  const chunk = (items, size) =>
    items.reduce((batches, item, index) => {
      if (index % size === 0) batches.push([]);
      batches[batches.length - 1].push(item);
      return batches;
    }, []);

  const firstUsableOffer = (connection) =>
    (connection?.nodes ?? []).find(
      (offer) => offer.fulfillmentType !== 'ON_DEMAND' && offer.channel === OFFER_CHANNEL
    ) ?? null;

  const pickOffer = (sdk, product) => {
    const liveOffer = firstUsableOffer(product.liveOffers);
    const stagedOffer = firstUsableOffer(product.stagedOffers);
    const stagedIsNewer = stagedOffer && (!liveOffer || stagedOffer.version > liveOffer.version);

    return sdk.previews?.isEnabled && stagedIsNewer ? stagedOffer : liveOffer;
  };

  const hasPersonalizationOffer = (sdk, product) => {
    const offer = pickOffer(sdk, product);

    return (
      Boolean(offer) &&
      AVAILABLE_OFFER_STATES.has(offer.activeState) &&
      (offer.options?.nodes?.length ?? 0) > 0
    );
  };

  const lookupBatch = async (sdk, variantIds) => {
    const { products } = await sdk.gql.queries.productsWithOffers({
      variables: {
        numProducts: variantIds.length,
        numImageAssets: 1,
        numOffers: 5,
        numOfferOptions: 1,
        offerChannels: [OFFER_CHANNEL],
        productsFilter: { productIds: variantIds },
      },
    });

    return (products?.nodes ?? []).reduce((availability, product) => {
      const isAvailable = hasPersonalizationOffer(sdk, product);
      [product.productId, product.id]
        .filter(Boolean)
        .forEach((key) => availability.set(String(key), isAvailable));
      return availability;
    }, new Map());
  };

  const lookupAvailability = (sdk, variantIds) => {
    const pending = variantIds.filter((variantId) => !availabilityByVariantId.has(variantId));

    chunk(pending, LOOKUP_BATCH_SIZE).forEach((batch) => {
      const batchAvailability = lookupBatch(sdk, batch).catch((error) => {
        console.error('Personalization filter lookup failed:', error);
        batch.forEach((variantId) => availabilityByVariantId.delete(variantId));
        return new Map();
      });

      batch.forEach((variantId) =>
        availabilityByVariantId.set(
          variantId,
          batchAvailability.then((availability) => availability.get(variantId) === true)
        )
      );
    });

    return Promise.all(
      variantIds.map((variantId) => availabilityByVariantId.get(variantId) ?? false)
    );
  };

  const variantIdFor = (swatch) => {
    if (swatch.hasAttribute(SOLD_OUT_ATTRIBUTE)) return '';
    if (swatch.dataset.variantId) return swatch.dataset.variantId;

    const productUrl = swatch.dataset.productUrl;
    if (!productUrl) return '';

    return new URL(productUrl, window.location.origin).searchParams.get('variant') ?? '';
  };

  const markSwatch = (swatch, isAvailable) =>
    swatch.setAttribute(RESOLVED_ATTRIBUTE, String(isAvailable));

  const applyFilter = (picker) => {
    const toggle = picker.querySelector(TOGGLE_SELECTOR);
    if (toggle) toggle.checked = filterEnabled;
    picker.classList.toggle(ACTIVE_CLASS, filterEnabled);
    if (!filterEnabled) return;

    const swatches = Array.from(picker.querySelectorAll(SWATCH_SELECTOR));
    const selected = swatches.find((swatch) => swatch.checked);
    if (selected?.getAttribute(RESOLVED_ATTRIBUTE) !== 'false') return;

    swatches.find((swatch) => swatch.getAttribute(RESOLVED_ATTRIBUTE) === 'true')?.click();
  };

  const updateFilterVisibility = (picker) => {
    if (picker !== document.querySelector(PICKER_SELECTOR)) return;

    const filter = picker.querySelector(FILTER_SELECTOR);
    if (!filter) return;

    const swatches = Array.from(picker.querySelectorAll(SWATCH_SELECTOR));
    const personalizable = swatches.filter(
      (swatch) => swatch.getAttribute(RESOLVED_ATTRIBUTE) === 'true'
    ).length;

    const resolving = swatches.some(
      (swatch) => !swatch.hasAttribute(RESOLVED_ATTRIBUTE) && variantIdFor(swatch)
    );
    if (personalizable === 0 && !resolving) filterEnabled = false;
    filter.hidden = personalizable === 0 || personalizable === swatches.length;
    applyFilter(picker);
  };

  const resolveSwatches = async (picker) => {
    const pending = Array.from(picker.querySelectorAll(SWATCH_SELECTOR)).filter(
      (swatch) => !swatch.hasAttribute(RESOLVED_ATTRIBUTE)
    );
    const [unresolved, unresolvable] = pending.reduce(
      ([withId, withoutId], swatch) =>
        variantIdFor(swatch) ? [[...withId, swatch], withoutId] : [withId, [...withoutId, swatch]],
      [[], []]
    );
    unresolvable.forEach((swatch) => markSwatch(swatch, false));
    if (!unresolved.length) {
      updateFilterVisibility(picker);
      return;
    }

    const sdk = await getSdk();
    if (!sdk) {
      unresolved.forEach((swatch) => markSwatch(swatch, false));
      updateFilterVisibility(picker);
      return;
    }

    const variantIds = [...new Set(unresolved.map(variantIdFor))];
    const availability = await lookupAvailability(sdk, variantIds);
    const availabilityByLookedUpId = new Map(
      variantIds.map((variantId, index) => [variantId, availability[index]])
    );

    unresolved.forEach((swatch) =>
      markSwatch(swatch, availabilityByLookedUpId.get(variantIdFor(swatch)) === true)
    );
    updateFilterVisibility(picker);
  };

  const logResolveError = (error) => console.error('Personalization filter failed:', error);

  const scheduleResolve = (picker) => {
    clearTimeout(resolveTimer);
    resolveTimer = setTimeout(() => resolveSwatches(picker).catch(logResolveError), RESOLVE_DEBOUNCE);
  };

  const initPicker = () => {
    const picker = document.querySelector(PICKER_SELECTOR);
    if (!picker || !picker.querySelector(FILTER_SELECTOR)) return;

    applyFilter(picker);
    new MutationObserver(() => scheduleResolve(picker)).observe(picker, {
      attributes: true,
      attributeFilter: ['data-product-url'],
      subtree: true,
    });
    resolveSwatches(picker).catch(logResolveError);
  };

  const handleToggle = (event) => {
    if (!event.target.matches(TOGGLE_SELECTOR)) return;

    filterEnabled = event.target.checked;
    const picker = event.target.closest(PICKER_SELECTOR);
    if (picker) applyFilter(picker);
  };

  const init = () => {
    document.addEventListener('change', handleToggle);
    document.addEventListener('variant-updated', initPicker);
    initPicker();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
